import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Droplets, Plus, Minus, Target, Trophy, Sparkles, Waves } from 'lucide-react';
import { SparkleGuide } from '@/components/guide/SparkleGuide';
import { useToast } from '@/hooks/use-toast';
import WhyThisMattersCard from '@/components/education/WhyThisMattersCard';
import { useWellnessEducation } from '@/hooks/useWellnessEducation';
import ConfettiCelebration from './ConfettiCelebration';

interface WaterTrackerProps {
  initialIntake?: number;
  target?: number;
  glassSize?: number;
  onUpdate: (intake: number, target: number, glassSize: number) => void;
}

export default function WaterTracker({ 
  initialIntake = 0, 
  target = 2000, 
  glassSize = 250,
  onUpdate 
}: WaterTrackerProps) {
  const [intake, setIntake] = useState(initialIntake);
  const [waterTarget, setWaterTarget] = useState(target);
  const [currentGlassSize, setCurrentGlassSize] = useState(glassSize);
  const [animateWater, setAnimateWater] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [glassCount, setGlassCount] = useState(Math.floor(intake / glassSize));
  const { toast } = useToast();
  const { data: education } = useWellnessEducation('water_tracking', 'hydration');

  const percentage = Math.min((intake / waterTarget) * 100, 100);
  const isGoalReached = intake >= waterTarget;
  const glassesNeeded = Math.ceil(waterTarget / currentGlassSize);

  useEffect(() => {
    setGlassCount(Math.floor(intake / currentGlassSize));
  }, [intake, currentGlassSize]);

  const addGlass = () => {
    const newIntake = intake + currentGlassSize;
    setIntake(newIntake);
    setAnimateWater(true);
    
    // Check if goal was just reached
    if (!isGoalReached && newIntake >= waterTarget) {
      setShowCelebration(true);
      toast({
        title: "🎉 Hydration Goal Achieved!",
        description: "Amazing! You've reached your daily water goal. Your body thanks you!",
      });
      setTimeout(() => setShowCelebration(false), 3000);
    } else {
      toast({
        title: "Glass added! 💧",
        description: `${currentGlassSize}ml added. Keep going!`
      });
    }

    setTimeout(() => setAnimateWater(false), 2000);
    onUpdate(newIntake, waterTarget, currentGlassSize);
  };

  const removeGlass = () => {
    if (intake > 0) {
      const newIntake = Math.max(0, intake - currentGlassSize);
      setIntake(newIntake);
      onUpdate(newIntake, waterTarget, currentGlassSize);
    }
  };

  const updateTarget = (newTarget: number) => {
    setWaterTarget(newTarget);
    onUpdate(intake, newTarget, currentGlassSize);
  };

  const updateGlassSize = (newSize: number) => {
    setCurrentGlassSize(newSize);
    onUpdate(intake, waterTarget, newSize);
  };

  const renderWaterGlasses = () => {
    const glasses = [];
    const totalGlasses = Math.max(glassesNeeded, Math.ceil(intake / currentGlassSize));
    
    for (let i = 0; i < totalGlasses; i++) {
      const isFilled = i < glassCount;
      const isPartiallyFilled = i === glassCount && (intake % currentGlassSize) > 0;
      const fillPercentage = isPartiallyFilled ? ((intake % currentGlassSize) / currentGlassSize) * 100 : 0;
      
      glasses.push(
        <div key={i} className="relative">
          {/* Glass container with proper containment */}
          <div className="w-12 h-16 border-2 border-primary/40 rounded-b-lg bg-background/30 relative overflow-hidden backdrop-blur-sm">
            {/* Glass shine effect */}
            <div className="absolute top-1 left-1 w-2 h-4 bg-white/20 rounded-full" />
            
            {/* Water fill with proper containment and wave effect */}
            <div 
              className={`absolute bottom-0 left-0 right-0 transition-all duration-1000 ease-out overflow-hidden ${
                animateWater && i <= glassCount ? 'animate-water-fill-complex' : ''
              }`}
              style={{ 
                height: isFilled ? '100%' : isPartiallyFilled ? `${fillPercentage}%` : '0%',
              }}
            >
              {/* Main water layer */}
              <div className="absolute inset-0 bg-gradient-to-t from-accent via-accent/90 to-accent/70" />
              
              {/* Water surface wave effect */}
              <div 
                className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-accent/80 via-accent to-accent/80 ${
                  (isFilled || isPartiallyFilled) ? 'animate-water-wave' : ''
                }`}
                style={{
                  clipPath: 'ellipse(100% 50% at 50% 50%)'
                }}
              />
              
              {/* Water bubbles */}
              {(isFilled || isPartiallyFilled) && (
                <>
                  <div className="absolute bottom-2 left-2 w-1 h-1 bg-accent-foreground/30 rounded-full animate-bubble-1" />
                  <div className="absolute bottom-4 right-3 w-0.5 h-0.5 bg-accent-foreground/20 rounded-full animate-bubble-2" />
                  <div className="absolute bottom-6 left-3 w-0.5 h-0.5 bg-accent-foreground/25 rounded-full animate-bubble-3" />
                </>
              )}
            </div>
            
            {/* Sparkle effect for filled glasses */}
            {isFilled && showCelebration && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <Sparkles className="w-4 h-4 text-primary animate-sparkle" />
              </div>
            )}
            
            {/* Glass reflection */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
          </div>
          
          {/* Glass number */}
          <div className="text-xs text-center mt-1 text-muted-foreground font-medium">
            {i + 1}
          </div>
        </div>
      );
    }
    
    return glasses;
  };

  return (
    <div className="space-y-6">
      <ConfettiCelebration isActive={showCelebration} particleCount={40} />
      
      <Card className={`p-6 transition-all duration-500 overflow-hidden relative ${
        showCelebration ? 'border-accent shadow-lg shadow-accent/20' : 'border-border'
      }`}>
        {/* Background water wave effect */}
        <motion.div
          animate={isGoalReached ? { y: [0, -5, 0] } : {}}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0 pointer-events-none"
        >
          <div 
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-accent/10 to-transparent transition-all duration-1000"
            style={{ height: `${percentage}%` }}
          />
        </motion.div>

        <div className="text-center mb-6 relative z-10">
          <motion.div 
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isGoalReached ? 'bg-gradient-to-br from-accent to-primary' : 'bg-primary/10'
            }`}
            animate={isGoalReached ? { 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            } : animateWater ? { scale: [1, 1.15, 1] } : {}}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={animateWater ? { y: [0, -5, 0] } : {}}
              transition={{ duration: 0.3 }}
            >
              {isGoalReached ? (
                <Trophy className="w-10 h-10 text-background" />
              ) : (
                <Droplets className="w-10 h-10 text-primary" />
              )}
            </motion.div>
          </motion.div>
          
          <div className="flex items-center justify-center gap-2 mb-2">
            <h3 className="text-2xl font-bold text-foreground">Water Intake</h3>
            <SparkleGuide
              title="How to track hydration"
              items={[
                { title: 'Set your target', description: 'Adjust your daily goal based on your body weight and activity level.' },
                { title: 'Tap to add glasses', description: 'Each tap adds one glass. See your progress fill up!' },
                { title: 'Track patterns', description: 'Over time, see how hydration affects your energy and mood.' },
                { title: 'Celebrate streaks', description: 'Reach your goal daily and watch the celebration!' }
              ]}
            />
          </div>
          
          {/* Animated counter */}
          <div className="flex items-center justify-center gap-1 mb-2">
            <motion.span
              key={intake}
              initial={{ scale: 1.3, color: 'hsl(var(--accent))' }}
              animate={{ scale: 1, color: 'hsl(var(--primary))' }}
              className="text-4xl font-bold"
            >
              {intake}
            </motion.span>
            <span className="text-xl text-muted-foreground">/ {waterTarget}ml</span>
          </div>
          
          <AnimatePresence>
            {isGoalReached && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2"
              >
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                >
                  <Trophy className="w-5 h-5 text-accent" />
                </motion.div>
                <span className="font-semibold text-accent">Hydration Goal Complete!</span>
                <motion.div
                  animate={{ rotate: [0, -15, 15, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                >
                  <Trophy className="w-5 h-5 text-accent" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Daily Goal Progress</span>
            <span>{Math.round(percentage)}%</span>
          </div>
          <Progress 
            value={percentage} 
            className={`h-4 transition-all duration-500 ${
              isGoalReached ? 'animate-success-glow' : ''
            }`}
          />
          <div className="text-center text-xs text-muted-foreground mt-2">
            {Math.max(0, waterTarget - intake)}ml remaining
          </div>
        </div>

        {/* Water Glasses Visualisation */}
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Your Glasses ({glassCount}/{glassesNeeded})
          </h4>
          <div className="flex flex-wrap gap-2 justify-center max-h-32 overflow-y-auto">
            {renderWaterGlasses()}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-4 relative z-10">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              onClick={addGlass}
              className="w-full flex items-center gap-2 group"
              variant={isGoalReached ? "default" : "outline"}
            >
              <motion.div
                animate={{ rotate: [0, 90, 0] }}
                transition={{ duration: 0.3 }}
                key={glassCount}
              >
                <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </motion.div>
              Add Glass ({currentGlassSize}ml)
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              onClick={removeGlass}
              variant="outline"
              disabled={intake === 0}
              className="w-full flex items-center gap-2"
            >
              <Minus className="w-4 h-4" />
              Remove Glass
            </Button>
          </motion.div>
        </div>

        {/* Settings */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
          <div>
            <Label className="text-xs font-medium">Glass Size (ml)</Label>
            <Input
              type="number"
              value={currentGlassSize}
              onChange={(e) => updateGlassSize(Number(e.target.value))}
              min={100}
              max={500}
              step={50}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Daily Target (ml)</Label>
            <Input
              type="number"
              value={waterTarget}
              onChange={(e) => updateTarget(Number(e.target.value))}
              min={1000}
              max={5000}
              step={250}
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      {education && (
        <WhyThisMattersCard
          toolName={education.tool_name}
          shortDescription={education.short_description}
          whyItMatters={education.why_it_matters}
          researchSummary={education.research_summary}
          howToOptimise={education.how_to_optimize}
          researchLinks={education.research_links}
          tags={education.tags}
        />
      )}
    </div>
  );
}